import { useRef, useState } from "react";
import { api, apiError } from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { StatusBadge, PriorityBadge } from "../badges";
import AttachmentsPanel from "../AttachmentsPanel";
import { toast } from "sonner";
import { Plus, Trash2, Paperclip, FileSpreadsheet, X, FileText } from "lucide-react";

const REQ_STATUSES = ["draft", "in_review", "approved"];
const EMPTY = { title: "", description: "", priority: "P2", verification_method: "Testing" };

export default function RequirementsTab({ project, requirements, tasks, reload }) {
  const [composing, setComposing] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: req } = await api.post(`/projects/${project.id}/requirements`, form);
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("entity_type", "requirement");
        fd.append("entity_id", req.id);
        try { await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch (err) { toast.error(`${f.name}: ${apiError(err)}`); }
      }
      toast.success(`${req.rid} created${files.length ? ` with ${files.length} attachment${files.length > 1 ? "s" : ""}` : ""}`);
      setForm(EMPTY); setFiles([]); setComposing(false);
      if (files.length) setExpanded(req.id);
      reload();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/requirements/${id}`, { status }); reload(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this requirement?")) return;
    try { await api.delete(`/requirements/${id}`); toast.success("Deleted"); reload(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const exportExcel = async () => {
    try {
      const r = await api.get(`/projects/${project.id}/requirements/export`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Requirements_${project.key}_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Requirements exported to Excel");
    } catch (err) { toast.error(apiError(err)); }
  };

  const linkedTasks = (rid) => tasks.filter((t) => t.requirement_id === rid);

  return (
    <div className="space-y-4" data-testid="requirements-tab-content">
      <div className="flex justify-end gap-2">
        <Button variant="outline" data-testid="requirements-export-btn" onClick={exportExcel} disabled={!requirements.length}>
          <FileSpreadsheet className="h-4 w-4 mr-1" /> Export Excel
        </Button>
        {!composing && (
          <Button data-testid="create-requirement-btn" onClick={() => setComposing(true)}>
            <Plus className="h-4 w-4 mr-1" /> New Requirement
          </Button>
        )}
      </div>

      {composing && (
        <Card className="p-5 border-primary/40 fade-in" data-testid="requirement-composer">
          <form onSubmit={create} className="space-y-3">
            <Input data-testid="requirement-title-input" placeholder="Requirement title — e.g. Inference latency < 20ms"
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required autoFocus />
            <Textarea data-testid="requirement-description-input" placeholder="Describe the requirement, acceptance criteria, constraints..."
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="min-h-[80px]" />
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {files.map((f, i) => (
                  <span key={i} data-testid="composer-file-chip"
                    className="flex items-center gap-1.5 text-xs font-mono border border-border rounded px-2 py-1 bg-accent/50">
                    <FileText className="h-3 w-3 text-muted-foreground" /> {f.name}
                    <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} data-testid="composer-file-remove-btn">
                      <X className="h-3 w-3 text-muted-foreground hover:text-rose-500" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="w-24 h-9" data-testid="requirement-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["P0", "P1", "P2", "P3"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.verification_method} onValueChange={(v) => setForm({ ...form, verification_method: v })}>
                <SelectTrigger className="w-40 h-9" data-testid="requirement-verification-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Testing", "Inspection", "Analysis", "Demonstration"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
              <input ref={fileRef} type="file" multiple className="hidden" data-testid="composer-file-input"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.svg,.txt,.md,.csv,.json,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.step,.stl,.dxf"
                onChange={(e) => { setFiles([...files, ...Array.from(e.target.files || [])]); e.target.value = ""; }} />
              <Button type="button" variant="outline" size="sm" data-testid="composer-attach-btn" onClick={() => fileRef.current?.click()}>
                <Paperclip className="h-3.5 w-3.5 mr-1" /> Attach {files.length > 0 && `(${files.length})`}
              </Button>
              <div className="ml-auto flex gap-2">
                <Button type="button" variant="ghost" size="sm" data-testid="composer-cancel-btn"
                  onClick={() => { setComposing(false); setForm(EMPTY); setFiles([]); }}>Cancel</Button>
                <Button type="submit" size="sm" data-testid="requirement-create-submit-btn" disabled={busy || !form.title.trim()}>
                  {busy ? "Creating..." : "Create Requirement"}
                </Button>
              </div>
            </div>
          </form>
        </Card>
      )}

      <div className="space-y-3 stagger">
        {requirements.map((r) => (
          <Card key={r.id} className="p-5" data-testid={`requirement-card-${r.rid.toLowerCase()}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-primary">{r.rid}</span>
                  <PriorityBadge priority={r.priority} />
                  <StatusBadge status={r.status} />
                  <span className="text-[10px] font-mono uppercase text-muted-foreground border border-border rounded px-1.5 py-0.5">{r.verification_method}</span>
                </div>
                <h3 className="font-heading font-semibold mt-1.5">{r.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{r.description}</p>
                {linkedTasks(r.id).length > 0 && (
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {linkedTasks(r.id).map((t) => (
                      <span key={t.id} className="text-[10px] font-mono border border-border rounded px-1.5 py-0.5 text-muted-foreground">{t.tid}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                  <SelectTrigger className="w-32 h-8 text-xs" data-testid="requirement-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{REQ_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="ghost" size="icon" data-testid="requirement-attachments-btn"
                  onClick={() => setExpanded(expanded === r.id ? null : r.id)} title="Attachments">
                  <Paperclip className={`h-4 w-4 ${expanded === r.id ? "text-primary" : "text-muted-foreground"}`} />
                </Button>
                <Button variant="ghost" size="icon" data-testid="requirement-delete-btn" onClick={() => remove(r.id)}>
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>
              </div>
            </div>
            {expanded === r.id && (
              <div className="mt-4 pt-4 border-t border-border">
                <AttachmentsPanel entityType="requirement" entityId={r.id} compact />
              </div>
            )}
          </Card>
        ))}
        {!requirements.length && !composing && <p className="text-sm text-muted-foreground">No requirements yet.</p>}
      </div>
    </div>
  );
}
